// Compact bar above the composer listing this conversation's pending
// scheduled messages, each cancelable. Keeps scheduled sends visible
// (and revocable) instead of invisible until they fire.

import React from "react";
import { Pressable, Text, View } from "react-native";
import { Clock, X } from "lucide-react-native";
import { useColorScheme } from "../../foundations/colorScheme";
import { tokens } from "../../design-system/tokens";
import { formatMessagePreview } from "../../appEvent";
import { formatScheduleShort } from "./schedulePresets";
import type { ScheduledMessage } from "./scheduledMessages";

export interface ScheduledMessagesBarProps {
  readonly messages: readonly ScheduledMessage[];
  readonly onCancel: (id: string) => void;
  readonly testID?: string;
}

export const ScheduledMessagesBar = React.memo<ScheduledMessagesBarProps>(
  ({ messages, onCancel, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
    const colors = tokens.color.semantic[colorScheme];

    if (messages.length === 0) return null;

    return (
      <View
        className="mx-sm mb-1 rounded-[12px] bg-light-surface-secondary dark:bg-dark-surface-elevated px-3 py-2 gap-1"
        testID={testID}
      >
        {messages.map((m) => (
          <View key={m.id} className="flex-row items-center gap-2">
            <Clock size={14} strokeWidth={2} color={colors.accent.indigo} />
            <Text
              className="font-sans text-[12px] font-semibold text-light-accent-indigo dark:text-[#8FA9FF]"
              numberOfLines={1}
            >
              {formatScheduleShort(new Date(m.sendAtMs))}
            </Text>
            <Text
              numberOfLines={1}
              className="flex-1 font-sans text-[13px] text-light-text-secondary dark:text-[#CCCCCC]"
            >
              {formatMessagePreview(m.text) ?? m.text}
            </Text>
            <Pressable
              onPress={() => onCancel(m.id)}
              accessibilityRole="button"
              accessibilityLabel="Cancel scheduled message"
              className="p-[2px] active:opacity-60"
              testID={`scheduled-cancel-${m.id}`}
            >
              <X size={16} strokeWidth={2} color={colors.text.secondary} />
            </Pressable>
          </View>
        ))}
      </View>
    );
  },
);

ScheduledMessagesBar.displayName = "ScheduledMessagesBar";
